/**
 * The building's chat: one room per complex, where residents and the committee
 * talk about what is coming up.
 *
 * It is not money, so it is ordinary documents and the rules do the policing.
 * Turning somebody's chat off, the cooldown between messages, locking the room
 * to the committee, pinning and deleting are all checked by the rules rather
 * than by a function, which keeps the room free to run: there is
 * nothing to bill but the reads and writes themselves.
 *
 * Three kinds of document, under the organization:
 *
 * - `chat/{messageId}`, one per message. The author's role and unit are
 *   checked against their claims, so a committee message cannot be faked and a
 *   renamed resident still shows which door they live behind.
 * - `chatMembers/{uid}`, one per person who has opened the room. Their chosen
 *   name, whether the committee has turned their chat off and until when, and
 *   when they last spoke, which is what the cooldown is measured from. Nobody
 *   deletes one: a resident whose chat is off still lives in the building and
 *   still reads the room.
 * - `chatSettings/room`, the committee's switches: the cooldown, whether only
 *   the committee may post, and the fundraisers pinned to the top.
 */

export const CHAT_MESSAGE_MAX = 500;
export const CHAT_NAME_MAX = 40;
/** The longest a committee may make somebody wait between messages: an hour. */
export const CHAT_COOLDOWN_MAX = 3600;
/** The waits a committee picks from, in seconds. */
export const CHAT_COOLDOWNS = [0, 10, 30, 60, 300] as const;
/** How many fundraisers can sit pinned above the room at once. */
export const CHAT_PINNED_CAMPAIGNS_MAX = 3;
/** How long the committee can turn somebody's chat off for, in seconds. Null is until turned back on. */
export const CHAT_OFF_SPANS = [3600, 86_400, 604_800, null] as const;
export type ChatOffSpan = (typeof CHAT_OFF_SPANS)[number];
/** How far back the room reads. Older messages stay stored and stop being read. */
export const CHAT_WINDOW = 30;

export const CHAT_ROOM = "room";

export type ChatRole = "resident" | "committee";

export interface ChatMessage {
  id: string;
  authorUid: string;
  role: ChatRole;
  /** The author's door, for a resident. Null for the committee. */
  unitId: string | null;
  /** What they chose to be called when they sent it, or "" for the default. */
  displayName: string;
  body: string;
  sentAt: Date | null;
  pinned: boolean;
}

export interface ChatMember {
  uid: string;
  role: ChatRole;
  unitId: string | null;
  displayName: string;
  /** The committee turned their chat off. Read with `chatIsOff`, which honours the end. */
  muted: boolean;
  /** When it comes back on by itself. Null, while muted, is until the committee says. */
  mutedUntil: Date | null;
  lastSentAt: Date | null;
}

export interface ChatSettings {
  cooldownSeconds: number;
  pinnedCampaigns: string[];
  /** Only the committee may post, for when the room needs to be quiet. */
  locked: boolean;
}

export const DEFAULT_CHAT_SETTINGS: ChatSettings = {
  cooldownSeconds: 0,
  pinnedCampaigns: [],
  locked: false,
};

export class ChatError extends Error {}

function dateOf(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

function roleOf(value: unknown): ChatRole {
  return value === "committee" ? "committee" : "resident";
}

function textOf(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function toChatMessage(id: string, data: Record<string, unknown>): ChatMessage {
  return {
    id,
    authorUid: textOf(data.authorUid),
    role: roleOf(data.appRole),
    unitId: typeof data.unitId === "string" ? data.unitId : null,
    displayName: textOf(data.displayName),
    body: textOf(data.body),
    sentAt: dateOf(data.sentAt),
    pinned: data.pinned === true,
  };
}

export function toChatMember(uid: string, data: Record<string, unknown>): ChatMember {
  return {
    uid,
    role: roleOf(data.appRole),
    unitId: typeof data.unitId === "string" ? data.unitId : null,
    displayName: textOf(data.displayName),
    muted: data.muted === true,
    mutedUntil: dateOf(data.mutedUntil),
    lastSentAt: dateOf(data.lastSentAt),
  };
}

export function toChatSettings(data: Record<string, unknown> | undefined): ChatSettings {
  if (!data) return DEFAULT_CHAT_SETTINGS;
  const cooldown = data.cooldownSeconds;
  const pinned = data.pinnedCampaigns;
  return {
    cooldownSeconds:
      typeof cooldown === "number" && Number.isInteger(cooldown) && cooldown > 0 ? cooldown : 0,
    pinnedCampaigns: Array.isArray(pinned)
      ? pinned.filter((id): id is string => typeof id === "string")
      : [],
    locked: data.locked === true,
  };
}

/** What somebody typed, checked and shaped the way the rules accept. */
export function chatMessageInput(body: string): string {
  const trimmed = body.trim();
  if (trimmed === "" || trimmed.length > CHAT_MESSAGE_MAX) {
    throw new ChatError(`A message is 1 to ${String(CHAT_MESSAGE_MAX)} characters.`);
  }
  return trimmed;
}

/**
 * A chosen name. Blank means the default: the door for a resident, the
 * committee's own label for a committee member.
 */
export function chatNameInput(name: string): string {
  const tidy = name.trim().replace(/\s+/g, " ");
  if (tidy.length > CHAT_NAME_MAX) {
    throw new ChatError(`A name is at most ${String(CHAT_NAME_MAX)} characters.`);
  }
  return tidy;
}

export function chatCooldownInput(seconds: number): number {
  if (!Number.isInteger(seconds) || seconds < 0 || seconds > CHAT_COOLDOWN_MAX) {
    throw new ChatError(`The wait between messages is 0 to ${String(CHAT_COOLDOWN_MAX)} seconds.`);
  }
  return seconds;
}

/** The fundraisers pinned once one more is pinned or taken off, oldest dropped first. */
export function pinCampaign(pinned: readonly string[], campaignId: string, on: boolean): string[] {
  const rest = pinned.filter((id) => id !== campaignId);
  if (!on) return rest;
  return [...rest, campaignId].slice(-CHAT_PINNED_CAMPAIGNS_MAX);
}

/** When a chat turned off for `span` comes back on, or null for until the committee says. */
export function chatOffUntil(span: ChatOffSpan, now: Date = new Date()): Date | null {
  return span === null ? null : new Date(now.getTime() + span * 1000);
}

/** Whether somebody's chat is off right now. A turned-off chat with an end passed is on again. */
export function chatIsOff(
  member: Pick<ChatMember, "role" | "muted" | "mutedUntil"> | null,
  now: Date = new Date(),
): boolean {
  if (!member || member.role === "committee" || !member.muted) return false;
  return member.mutedUntil === null || member.mutedUntil.getTime() > now.getTime();
}

export type ChatVoice = "speak" | "locked" | "off";

/** Whether somebody may post at all, before the cooldown is counted. The committee always may. */
export function chatVoice(
  member: Pick<ChatMember, "role" | "muted" | "mutedUntil"> | null,
  settings: Pick<ChatSettings, "locked">,
  now: Date = new Date(),
): ChatVoice {
  if (member?.role === "committee") return "speak";
  if (chatIsOff(member, now)) return "off";
  return settings.locked ? "locked" : "speak";
}

/**
 * Whole seconds before somebody may speak again. The committee is never made
 * to wait: the cooldown is theirs to set, not to sit out.
 */
export function secondsToWait(
  member: Pick<ChatMember, "role" | "lastSentAt"> | null,
  cooldownSeconds: number,
  now: Date = new Date(),
): number {
  if (!member || member.role === "committee" || !member.lastSentAt || cooldownSeconds <= 0) {
    return 0;
  }
  const due = member.lastSentAt.getTime() + cooldownSeconds * 1000;
  return Math.max(0, Math.ceil((due - now.getTime()) / 1000));
}

/** Oldest first, the way a room is read, with anything not yet stamped at the end. */
export function inOrder(messages: readonly ChatMessage[]): ChatMessage[] {
  return [...messages].sort(
    (a, b) =>
      (a.sentAt?.getTime() ?? Number.MAX_SAFE_INTEGER) -
      (b.sentAt?.getTime() ?? Number.MAX_SAFE_INTEGER),
  );
}

/**
 * Above this many unread messages the count stops counting and says "10+".
 * Past ten the number has stopped telling anybody anything they would act on.
 */
export const CHAT_UNREAD_CAP = 10;
/**
 * How many of the newest messages the unread count reads. More than the cap,
 * so the reader's own messages among the newest still leave enough to say
 * "10+" honestly.
 */
export const CHAT_UNREAD_WINDOW = 20;

/**
 * How many messages somebody has not seen: sent after the moment they last had
 * the chat open, by anybody but them. A message not yet stamped is the
 * reader's own a moment after sending, so it never counts. Null for `seenAt`
 * is a device that has not yet read back when it last looked, which counts as
 * nothing unread rather than everything.
 */
export function unreadChat(
  messages: readonly Pick<ChatMessage, "authorUid" | "sentAt">[],
  seenAt: number | null,
  uid: string,
): number {
  if (seenAt === null) return 0;
  return messages.filter(
    (message) =>
      message.authorUid !== uid && message.sentAt !== null && message.sentAt.getTime() > seenAt,
  ).length;
}

/** The count as the red mark shows it: nothing at zero, "10+" past the cap. */
export function unreadLabel(count: number): string {
  if (count <= 0) return "";
  return count > CHAT_UNREAD_CAP ? `${String(CHAT_UNREAD_CAP)}+` : String(count);
}

/** The newest stamped message's time, for marking the chat seen up to it. */
export function newestSent(messages: readonly Pick<ChatMessage, "sentAt">[]): number {
  return messages.reduce((latest, message) => Math.max(latest, message.sentAt?.getTime() ?? 0), 0);
}
